"use client";

import { c as _c } from "react/compiler-runtime";
import { getProjectProposalIds } from "@agroproj/backend/convex/assistant/proposalParts";
import { getToolName, isToolUIPart } from "ai";
import { CheckIcon, CopyIcon, ThumbsDownIcon, ThumbsUpIcon } from "lucide-react";
import type { Route } from "next";
import Link from "next/link";
import { useEffect, useMemo, useState, type ComponentProps } from "react";
import { Streamdown, type Components } from "streamdown";
import { AssistantProjectConfirmation } from "@/components/assistant/assistant-project-confirmation";
import { Button } from "@/components/ui/button";
import type { AssistantToolName, AssistantUIMessage } from "@/hooks/use-assistant-chat";
type Part = AssistantUIMessage["parts"][number];
type ToolPart = Extract<Part, {
  toolCallId: string;
}>;
export type AssistantFeedback = "positive" | "negative";

// Checked against the backend tool set, so a new read tool fails typecheck until it gets a
// label. Project action tools come from a `ToolSet`-typed factory, so they are listed by name.
const TOOL_LABELS = {
  getCurrentOrganizationContext: "Verificando a organização",
  getOperationalOverview: "Consultando o resumo operacional",
  findProjectsNeedingAttention: "Buscando projetos que precisam de atenção",
  searchProjects: "Buscando projetos",
  getCurrentProjectBrief: "Lendo o projeto aberto",
  searchProducers: "Buscando produtores",
  getCurrentProducerBrief: "Lendo o produtor aberto",
  getFinanceDueSummary: "Consultando vencimentos",
  getCrmFollowUpSummary: "Consultando follow-ups do CRM",
  getOfficialAgroDigest: "Lendo atualizações oficiais",
  getSectorNewsDigest: "Lendo notícias do setor",
  resolveCurrentAppRoute: "Identificando a página atual",
  searchProjectReferences: "Buscando referências do projeto",
  prepareCreateProject: "Preparando a revisão do projeto",
  prepareDuplicateProject: "Preparando a duplicação do projeto"
} satisfies Record<AssistantToolName, string> & Record<string, string>;
const toolLabelsByName: Partial<Record<string, string>> = TOOL_LABELS;
function getToolLabel(part: ToolPart) {
  return toolLabelsByName[getToolName(part)] ?? "Consultando dados";
}

/** Text of a message, used for copy and for deciding whether anything is visible yet. */
function getMessageText(message: AssistantUIMessage) {
  return message.parts.filter(part => part.type === "text").map(part => part.text).join("\n\n").trim();
}
export function hasVisibleContent(message: AssistantUIMessage) {
  return getMessageText(message).length > 0 || message.parts.some(isToolUIPart) || (message.metadata?.proposalIds?.length ?? 0) > 0;
}

/** Groups consecutive tool parts so each run of tool calls renders as one step list. */
function groupParts(parts: Part[]) {
  const groups: Array<{
    kind: "text";
    key: string;
    text: string;
  } | {
    kind: "tools";
    key: string;
    parts: ToolPart[];
  }> = [];
  parts.forEach((part, index) => {
    if (part.type === "text") {
      if (part.text.trim()) groups.push({
        kind: "text",
        key: `text-${index}`,
        text: part.text
      });
      return;
    }
    if (!isToolUIPart(part)) return;
    const last = groups.at(-1);
    if (last?.kind === "tools") last.parts.push(part);else groups.push({
      kind: "tools",
      key: part.toolCallId,
      parts: [part]
    });
  });
  return groups;
}
function ToolSteps(t0) {
  const $ = _c(4);
  const {
    parts
  } = t0;
  let t1;
  if ($[0] !== parts) {
    t1 = parts.map(_temp);
    $[0] = parts;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ul className="text-muted-foreground flex flex-col gap-0.5 text-xs/relaxed">{t1}</ul>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(part) {
  const label = getToolLabel(part);
  return <li key={part.toolCallId}>{part.state === "output-error" ? `${label}: falhou` : part.state === "output-available" ? label : `${label}...`}</li>;
}
function MarkdownLink(t0) {
  const $ = _c(9);
  const {
    href,
    children,
    onNavigate
  } = t0;
  if (href?.startsWith("/") && !href.startsWith("//")) {
    const t1 = href as Route;
    let t2;
    if ($[0] !== children || $[1] !== onNavigate || $[2] !== t1) {
      t2 = <Link className="text-foreground underline underline-offset-4" href={t1} onClick={onNavigate}>{children}</Link>;
      $[0] = children;
      $[1] = onNavigate;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (href && /^https?:\/\//i.test(href)) {
    let t1;
    if ($[4] !== children || $[5] !== href) {
      t1 = <a className="text-foreground underline underline-offset-4" href={href} rel="noopener noreferrer" target="_blank">{children}</a>;
      $[4] = children;
      $[5] = href;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    return t1;
  }
  let t1;
  if ($[7] !== children) {
    t1 = <span>{children}</span>;
    $[7] = children;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}
function CopyButton(t0) {
  const $ = _c(11);
  const {
    text
  } = t0;
  const [copied, setCopied] = useState(false);
  let t1;
  let t2;
  if ($[0] !== copied) {
    t1 = () => {
      if (!copied) {
        return;
      }
      const timeout = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timeout);
    };
    t2 = [copied];
    $[0] = copied;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const t3 = copied ? "Resposta copiada" : "Copiar resposta";
  let t4;
  if ($[3] !== text) {
    t4 = () => {
      navigator.clipboard.writeText(text).then(() => setCopied(true));
    };
    $[3] = text;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== copied) {
    t5 = copied ? <CheckIcon /> : <CopyIcon />;
    $[5] = copied;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t4 || $[9] !== t5) {
    t6 = <Button aria-label={t3} onClick={t4} size="icon-sm" variant="ghost">{t5}</Button>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
export function AssistantMessage(t0) {
  const $ = _c(35);
  const {
    message,
    isStreaming,
    feedback,
    onFeedback,
    onNavigate
  } = t0;
  let t1;
  if ($[0] !== onNavigate) {
    t1 = {
      a: props => <MarkdownLink {...props} onNavigate={onNavigate} />
    };
    $[0] = onNavigate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const components = t1;
  let t2;
  if ($[2] !== message) {
    t2 = getMessageText(message);
    $[2] = message;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const text = t2;
  if (message.role === "user") {
    if (!text) {
      return null;
    }
    let t3;
    if ($[4] !== text) {
      t3 = <div className="bg-muted ml-auto max-w-[85%] rounded-lg px-3 py-2 text-sm/relaxed break-words whitespace-pre-wrap">{text}</div>;
      $[4] = text;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t3;
  if ($[6] !== message.metadata?.proposalIds || $[7] !== message.parts) {
    let t4;
    if ($[9] !== message.metadata?.proposalIds) {
      t4 = message.metadata?.proposalIds ?? [];
      $[9] = message.metadata?.proposalIds;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t3 = new Set([...getProjectProposalIds(message.parts), ...t4]);
    $[6] = message.metadata?.proposalIds;
    $[7] = message.parts;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[11] !== t3) {
    t4 = [...t3];
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const proposalIds = t4;
  let t5;
  let t6;
  let t7;
  if ($[13] !== components || $[14] !== isStreaming || $[15] !== message.parts || $[16] !== proposalIds) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const groups = groupParts(message.parts);
      if (groups.length === 0 && proposalIds.length === 0) {
        t7 = null;
        break bb0;
      }
      t5 = "flex min-w-0 flex-col gap-2 text-sm/relaxed";
      let t8;
      if ($[20] !== components || $[21] !== isStreaming) {
        t8 = group => group.kind === "tools" ? <ToolSteps key={group.key} parts={group.parts} /> : <Streamdown className="min-w-0 break-words" components={components} controls={false} isAnimating={isStreaming} key={group.key} linkSafety={{
          enabled: false
        }} mode={isStreaming ? "streaming" : "static"}>{group.text}</Streamdown>;
        $[20] = components;
        $[21] = isStreaming;
        $[22] = t8;
      } else {
        t8 = $[22];
      }
      t6 = groups.map(t8);
    }
    $[13] = components;
    $[14] = isStreaming;
    $[15] = message.parts;
    $[16] = proposalIds;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[23] !== proposalIds) {
    t8 = proposalIds.map(_temp2);
    $[23] = proposalIds;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== feedback || $[26] !== isStreaming || $[27] !== onFeedback || $[28] !== text) {
    t9 = !isStreaming && text ? <div className="-ml-2 flex items-center gap-0.5"><CopyButton text={text} /><Button aria-label={"Resposta \xFAtil"} aria-pressed={feedback === "positive"} onClick={() => onFeedback("positive")} size="icon-sm" variant={feedback === "positive" ? "secondary" : "ghost"}><ThumbsUpIcon /></Button><Button aria-label={"Resposta pouco \xFAtil"} aria-pressed={feedback === "negative"} onClick={() => onFeedback("negative")} size="icon-sm" variant={feedback === "negative" ? "secondary" : "ghost"}><ThumbsDownIcon /></Button></div> : null;
    $[25] = feedback;
    $[26] = isStreaming;
    $[27] = onFeedback;
    $[28] = text;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t5 || $[31] !== t6 || $[32] !== t8 || $[33] !== t9) {
    t10 = <div className={t5}>{t6}{t8}{t9}</div>;
    $[30] = t5;
    $[31] = t6;
    $[32] = t8;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  return t10;
}
function _temp2(proposalId) {
  return <AssistantProjectConfirmation key={proposalId} proposalId={proposalId} />;
}